/** importing libraries  **/
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

/** importing dependencies  **/
import { RootState } from "../store";
import { IPost } from "../../interfaces/interfaces";
import { postApi } from "../../services/apis/post.api";

interface PostState {
    posts: IPost[];
    userPosts: IPost[];
    organizationPosts: IPost[];
}

const initialState: PostState = {
    posts: [],
    userPosts: [],
    organizationPosts: [],
};

/**
 * @description - To fetch all post
 */
const fetchAllPosts = createAsyncThunk(
    "posts/fetchAll",
    async (_, { rejectWithValue, getState, dispatch }) => {
        try {
            const { access_token } = (getState() as RootState).auth;

            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const data = await postApi.getAll(header);
            dispatch(setPosts(data?.data));
            dispatch(setUserPosts(data?.data));
            return data;
        } catch (error: any) {
            return rejectWithValue(error.data.data);
        }
    }
);

/**
 * @description - To fetch all post from a user ID user id
 */
type FetchUserPostsArg =
    | string
    | {
          userId: string;
          organizationId?: string;
          offset?: number;
          limit?: number;
      };

const fetchUserPosts = createAsyncThunk(
    "posts/fetchUser",
    async (arg: FetchUserPostsArg, { rejectWithValue, getState, dispatch }) => {
        try {
            const { access_token } = (getState() as RootState).auth;

            const header = {
                Authorization: `Bearer ${access_token}`,
            };

            const params = typeof arg === "string" ? { userId: arg } : arg;
            const { userId, organizationId, offset = 0, limit = 20 } =
                params as {
                    userId: string;
                    organizationId?: string;
                    offset?: number;
                    limit?: number;
                };

            const data = await postApi.getUserPosts(
                userId,
                header,
                offset,
                limit,
                organizationId
            );

            dispatch(setUserPosts(data.data));
            return data;
        } catch (error: any) {
            console.error(error);
            return rejectWithValue(error.data.data);
        }
    }
);

/**
 * @description - To fetch posts by organization and filter based on the selected organization
 */
const fetchPostsByOrganization = createAsyncThunk(
    "posts/fetchPostsByOrganization",
    async (
        { organizationId, offset = 0, limit = 20 }: { organizationId: string; offset?: number; limit?: number },
        { getState, dispatch, rejectWithValue }
    ) => {
        try {
            const { access_token } = (getState() as RootState).auth;
            const header = { Authorization: `Bearer ${access_token}` };

            const response = await postApi.getAllPostByOrganization(
                organizationId,
                header,
                offset,
                limit
            );


            dispatch(
                setPostsByOrganization({
                    organizationId,
                    posts: response.data || [],
                })
            );

            return response.data;
        } catch (error: any) {
            console.error("Failed to fetch posts:", error);
            return rejectWithValue(error?.response?.data || "Error fetching posts");
        }
    }
);


/**
 * @description - To create a post
 */
const createPost = createAsyncThunk(
    "posts/create",
    async (post: any, { rejectWithValue, getState, dispatch }) => {
        try {
            const { access_token } = (getState() as RootState).auth;

            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const data = await postApi.create(post, header);
            dispatch(setPosts([data?.data]));
            dispatch(setUserPosts([data?.data]));
            dispatch(setPostsByOrganization({ organizationId: data?.data.organizationId, posts: [data?.data] }));
            return data;
        } catch (error: any) {
            return rejectWithValue(error.data.data);
        }
    }
);

/**
 * @description - To delete a post
 */
const deletePost = createAsyncThunk(
    "posts/delete",
    async (postId: string, { rejectWithValue, getState, dispatch }) => {
        try {
            const { access_token } = (getState() as RootState).auth;

            const header = {
                Authorization: `Bearer ${access_token}`,
            };

            const data = await postApi.delete(postId, header);
            dispatch(removePost(postId));
            return data;
        } catch (error: any) {
            return rejectWithValue(error.data.data);
        }
    }
);

/**
 * @description - To pin a  post
 */
const pinPost = createAsyncThunk(
    "posts/pin",
    async (postId: string, { rejectWithValue, getState, dispatch }) => {
        try {
            const { access_token } = (getState() as RootState).auth;

            const header = {
                Authorization: `Bearer ${access_token}`,
            };

            const data = await postApi.pin(postId, header);
            dispatch(savePostAction({ post: postId, user: data.data.user }));
            return data;
        } catch (error: any) {
            return rejectWithValue(error.data.data);
        }
    }
);

/**
 * @description - To save a post
 */
const savePost = createAsyncThunk(
    "posts/save",
    async (postId: string, { rejectWithValue, getState, dispatch }) => {
        try {
            const { access_token } = (getState() as RootState).auth;

            const header = {
                Authorization: `Bearer ${access_token}`,
            };

            const data = await postApi.save(postId, header);
            dispatch(savePostAction(data.data));
            return data;
        } catch (error: any) {
            return rejectWithValue(error.data.data);
        }
    }
);

/**
 * @description - To comment on a post
 */
const commentOnPost = createAsyncThunk(
    "posts/comment",
    async ({ postId, body }: any, { rejectWithValue, getState, dispatch }) => {
        try {
            const { access_token } = (getState() as RootState).auth;

            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const data = await postApi.comment(body, header);
            // The endpoint returns the post's full comments array.
            dispatch(
                updatePostFields({ postId: postId, changes: { comments: data.data } })
            );
            return data;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data ?? error);
        }
    }
);

const postSlice = createSlice({
    name: "posts",
    initialState,
    reducers: {
        /**
         * Set All Posts in state
         */
        setPosts: (state, action: PayloadAction<IPost[]>) => {
            // Filter out duplicates
            const newPosts = action.payload.filter(
                (post) => !state.posts.some((p) => p._id === post._id)
            );

            // Prepend new posts so they go to the top
            state.posts.unshift(...newPosts);

            // (Optional) Sort by createdAt descending to keep them in strict date order
            state.posts.sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );

            // Force new reference for correct re-render
            state.posts = [...state.posts];
        },

        /**
         * Remove post from state
         */
        removePost: (state, action) => {
            state.posts = state.posts.filter(
                (post) => post._id !== action.payload
            );
        },
        /**
         * Pin a post
         */
        pinPostAction: (state, action) => {
            state.posts = state.posts.map((post) =>
                post._id === action.payload
                    ? { ...post, checkPinned: true }
                    : post
            );
        },
        /**
         * Save a post
         */
        savePostAction: (state, action: PayloadAction<{ user: any; post: any }>) => {
            const { post } = action.payload;
            if (!post) return;

            const updateList = (list: typeof state.posts) => {
                const idx = list.findIndex(p => p._id === post._id);
                if (idx !== -1) {
                    // Update the post with the new savedBy array
                    list[idx] = {
                        ...list[idx],
                        savedBy: post.savedBy,
                    };
                }
            };

            updateList(state.posts);
            updateList(state.userPosts);
            updateList(state.organizationPosts);
        },


        /**
         * Patch one post wherever it is held. The three lists are separate
         * copies, so a change made on the detail page (a new comment, a view
         * counted) has to land in all of them or the feed card goes stale
         * the moment the user navigates back.
         */
        updatePostFields: (
            state,
            action: PayloadAction<{ postId: string; changes: Partial<IPost> }>
        ) => {
            const { postId, changes } = action.payload;
            if (!postId || !changes) return;
            const patch = (list: IPost[]) => {
                const idx = list.findIndex((p) => p._id === postId);
                if (idx !== -1) list[idx] = { ...list[idx], ...changes };
            };
            patch(state.posts);
            patch(state.userPosts);
            patch(state.organizationPosts);
        },
        /**
         * comment on a post. Kept for callers that dispatch it directly; the
         * payload's `comment` is the full comments array from the server.
         */
        commentOnPostAction: (state, action) => {
            const { postId, comment } = action.payload;
            const comments = Array.isArray(comment) ? comment : null;
            if (!comments) return;
            const patch = (list: IPost[]) => {
                const idx = list.findIndex((p) => p._id === postId);
                if (idx !== -1) list[idx] = { ...list[idx], comments };
            };
            patch(state.posts);
            patch(state.userPosts);
            patch(state.organizationPosts);
        },
        /**
         * Set a user post in a state
         */
        setUserPosts: (state, action: PayloadAction<IPost[]>) => {
            const uniquePosts = action.payload.filter(
                (post) => !state.userPosts.some((p) => p._id === post._id)
            );
            // Prepend instead of push
            state.userPosts.unshift(...uniquePosts);

            // (Optional) sort descending
            state.userPosts.sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );

            // Force new reference
            state.userPosts = [...state.userPosts];
        },

        /**
         * Set posts by organization
         */
        setPostsByOrganization: (
            state,
            action: PayloadAction<{ organizationId: string; posts: IPost[] }>
        ) => {
            const { organizationId, posts } = action.payload;

            // Filter to keep only posts matching this organization
            const filteredPosts = posts.filter(
                (p) =>
                    p.organizationId === organizationId ||
                    p.user?.organization?._id === organizationId
            );

            // Filter out duplicates
            const uniqueNewPosts = filteredPosts.filter(
                (newPost) =>
                    !state.organizationPosts.some((existing) => existing._id === newPost._id)
            );

            // Prepend new posts
            state.organizationPosts.unshift(...uniqueNewPosts);

            // Sort descending by createdAt
            state.organizationPosts.sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );

            // For immutability
            state.organizationPosts = [...state.organizationPosts];
        },




    },
});

export function getPostState(state: RootState): PostState {
    return state.posts;
}

const {
    setUserPosts,
    setPosts,
    setPostsByOrganization,
    commentOnPostAction,
    updatePostFields,
    removePost,
    pinPostAction,
    savePostAction,
} = postSlice.actions;

export {
    setUserPosts,
    setPosts,
    setPostsByOrganization,
    commentOnPostAction,
    updatePostFields,
    removePost,
    pinPostAction,
    fetchAllPosts,
    fetchUserPosts,
    fetchPostsByOrganization,
    createPost,
    deletePost,
    pinPost,
    savePost,
    commentOnPost,
};
export const postReducer = postSlice.reducer;
